import {
	type ClientPropertyArtDetailResponseDTO,
	type ClientPropertyCottageDetailResponseDTO,
	type ClientPropertyEstimatedPriceHistoryResponseDTO,
	type ClientPropertyFlatDetailResponseDTO,
	type ClientPropertyHouseDetailResponseDTO,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	type useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { REAL_ESTATE_TYPES } from "web_myplann/Portfolio/Helpers/realEstateTypes";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type TranslateFn = ReturnType<typeof useResource>["t"];

type PropertyDetail =
	| ClientPropertyVehicleDetailResponseDTO
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO
	| ClientPropertyLandDetailResponseDTO
	| ClientPropertyArtDetailResponseDTO;

type PropertyHeroMeta = {
	subtype: string;
	valueLabel: string;
	address?: string;
	estimatedValue?: number | null;
	valueSource?: string;
	evaluationPercent?: number | null;
	priceHistory?: ClientPropertyEstimatedPriceHistoryResponseDTO[];
};

type LocatedProperty =
	| ClientPropertyLandDetailResponseDTO
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO;

const isRealEstate = (property: PropertyDetail): property is LocatedProperty =>
	REAL_ESTATE_TYPES.has(property.type);

const isLand = (
	property: LocatedProperty,
): property is ClientPropertyLandDetailResponseDTO =>
	property.type === ClientPropertyTypeEnum.Land;

const buildAddress = (property: LocatedProperty): string | undefined => {
	const address = [
		property.location,
		property.locationMunicipality,
		property.locationZipCode,
	]
		.filter(Boolean)
		.join(", ");

	return address || undefined;
};

const buildValueSource = (
	property: LocatedProperty,
	t: TranslateFn,
): string | undefined => {
	if (!property.source || !property.propertyValueDate) {
		return undefined;
	}

	return t(Resources.PortfolioDetail.Property.ValueSourceEstimate, {
		source: property.source,
		date: dateFormatter(property.propertyValueDate),
	});
};

export const getPropertyHeroMeta = (
	property: PropertyDetail,
	t: TranslateFn,
): PropertyHeroMeta => {
	const subtype = t(
		Resources.PortfolioDetail.Property.PropertyGroup[property.type],
	);

	if (!isRealEstate(property)) {
		return {
			subtype,
			valueLabel: t(Resources.PortfolioDetail.Property.CurrentValue),
			estimatedValue: property.value,
			evaluationPercent: property.evaluationPercent,
		};
	}

	const estimatedValue = isLand(property)
		? property.landValue
		: property.marketValue;

	return {
		subtype,
		valueLabel: t(Resources.PortfolioDetail.Property.MarketPrice),
		address: buildAddress(property),
		estimatedValue,
		valueSource: buildValueSource(property, t),
		evaluationPercent: property.evaluationPercent,
		priceHistory: property.estimatedPriceHistoryList,
	};
};
